import { MouseEvent, useEffect, useRef, useState } from "react";
import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import { classNames, debounce, useIsMounted } from "@ct-react/core";
import { Image } from "@shared/graphql";
import { Ratio } from "../../models/common";
import { DataProps } from "../../tools/components";
import RatioImage from "./ratio-image";
import "./images-carousel.scss";

type Props = DataProps<Image[]> & {
  ratio: Ratio;
  lazy?: boolean;
}

const ImagesCarousel = (
  {
    className,
    ratio,
    lazy = true,
    data
  }: Props) => {

  const isMounted = useIsMounted();
  const [ index, setIndex ] = useState<number>(0);
  const [ length, setLength ] = useState<number>(data.length);

  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderTrackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMounted) return;
    setIndex(0);
    setLength(data.length ?? 1);
  }, [ data ]);

  const onSliderScroll = debounce(() => {
    const newIndex = Math.round(sliderTrackRef.current!.scrollLeft / sliderRef.current!.scrollWidth);
    setIndex(newIndex);
  });

  const onSliderNav = (e: MouseEvent, dir: -1 | 1) => {
    e.preventDefault();
    if ((dir === -1 && index === 0) || (dir === 1 && index + 1 === length))
      return;
    sliderTrackRef.current!.scroll({
      behavior: "smooth",
      left: sliderTrackRef.current!.scrollLeft + (dir * sliderRef.current!.scrollWidth)
    });
  }

  const wrapperClasses = classNames("carousel-wrapper", className);

  return (
    <div ref={sliderRef}
         className={wrapperClasses}>

      <div ref={sliderTrackRef}
           className="carousel-track"
           onScrollCapture={onSliderScroll}>
        {data.map((img, i) => (<RatioImage key={i}
                                           className="carousel-item"
                                           source={img.assets}
                                           ratio={ratio}
                                           lazy={lazy} />)
        )}
      </div>

      {(length > 0) &&
        <>
          <button className={classNames("carousel-arrow", "left", { mask: index === 0 })}
                  tabIndex={-1}
                  onClick={e => onSliderNav(e, -1)}>
            <IconArrowLeft className="a-icon" />
          </button>
          <button className={classNames("carousel-arrow", "right", { mask: index + 1 === length })}
                  tabIndex={-1}
                  onClick={e => onSliderNav(e, 1)}>
            <IconArrowRight className="a-icon" />
          </button>
        </>
      }

    </div>);

}

export default ImagesCarousel;
